import * as React from 'react'
import type { Completion } from '@codemirror/autocomplete'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { CodeMirrorWithVariables, CodeMirrorWithVariablesProps } from './common'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import { clsx } from 'clsx'

import s from './single-line-input.module.scss'

export type VariablesInputProps = Pick<
  CodeMirrorWithVariablesProps,
  'getVariables' | 'autoFocus' | 'disabled' | 'placeholder' | 'id' | 'hideInfo'
> & {
  value: string
  setValue: (value: string) => void | Promise<void>
  variant?: 'input' | 'textarea'
  onSubmit?: () => void
  className?: string
  disableSyncValueRateLimit?: boolean
}

export type GetVariables = VariablesInputProps['getVariables']
export type Variable = Completion

export const VariablesInput = (props: VariablesInputProps) => {
  const { value, onChange, sync } = useSyncedValue({
    value: props.value,
    setValue: (v) => {
      props.setValue(v)
    },
    enabled: !props.disabled,
    rateLimitType: props.disableSyncValueRateLimit ? 'disabled' : undefined,
  })

  /** onChange triggers before onKeyDown, for some reason, so we keep track of this to see if the Enter is meant to be a submit, or other things like "accept suggestion". */
  const cleanedUpNewLineInLastInput = React.useRef(false)

  return (
    <CodeMirrorWithVariables
      data-native-undo-redo
      className={clsx(
        props.className,
        s.container,
        s['variant-' + (props.variant || 'input')]
      )}
      getVariables={props.getVariables}
      placeholder={props.placeholder}
      autoFocus={props.autoFocus}
      data-disabled={props.disabled}
      readOnly={props.disabled}
      hideInfo={props.hideInfo}
      value={value}
      indentWithTab={false}
      tabIndex={0}
      onFocus={(e) => {
        e.currentTarget
          .querySelector<HTMLDivElement>('[contenteditable="true"]')
          ?.focus()
      }}
      onKeyDown={(e) => {
        // if (isHotkey('mod+z', e) || isHotkey('ctrl+z', e)) {
        //   e.stopPropagation()
        // }
        if (props.variant === 'input') {
          if (e.key === 'Enter' && cleanedUpNewLineInLastInput.current) {
            e.preventDefault()
            props.onSubmit?.()
          }
        } else if (props.variant === 'textarea') {
          if (e.key === 'Enter' && commandKeyPressed(e)) {
            e.preventDefault()
            props.onSubmit?.()
          }
        }
      }}
      onBlur={() => {
        sync()
      }}
      onChange={(value) => {
        if (props.variant === 'input') {
          const originalValue = value
          // cleanup newlines
          value = value.replace(/\n/g, '')
          cleanedUpNewLineInLastInput.current = originalValue !== value
        }
        onChange(value)
      }}
    />
  )
}
